import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from 'antd';
import {
  LeftOutlined, RightOutlined, FullscreenOutlined, FullscreenExitOutlined,
  PlayCircleOutlined, PauseCircleOutlined,
} from '@ant-design/icons';

const { Title, Paragraph } = Typography;

function hexToRgba(hex, alpha) {
  const h = (hex || '#00E5FF').replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

const isPageNumber = (l) => {
  const m = l.match(/^(\d+)\s*\/\s*(\d+)$/);
  return m && parseInt(m[1]) <= parseInt(m[2]);
};
const isJunk = (l) =>
  /^ALAYAESS/.test(l) || l === 'Confidential' || isPageNumber(l) ||
  /^[A-Z]\s*$/.test(l) || /銷售簡報\s*v\d/.test(l) || /Sales\s*Pitch/i.test(l) ||
  /^AlayaEss\s*·/i.test(l) || /^ALAYAESS\s*·/.test(l);

const isEmojiOnly = (l) => /^[\u{1F300}-\u{1FAD6}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F900}-\u{1F9FF}\u{200D}\u{20E3}]+$/u.test(l);
const startsWithEmoji = (l) => {
  const m = l.match(/^([\u{1F300}-\u{1FAD6}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F900}-\u{1F9FF}]+)\s+(.+)/u);
  return m ? { emoji: m[1], rest: m[2] } : null;
};
const isStat = (l) => /^\$[\d,.]+/.test(l) || /^\d+%/.test(l) || /^\d+\s*個月$/.test(l) || /^無價$/.test(l) || /^∞$/.test(l) || /^無上限$/.test(l) || /^365\s*\/\s*24$/.test(l);
const isStepNum = (l) => /^(0[1-9]|Day\s*\d+)$/i.test(l);
const isPlanName = (l) => /^(STARTER|GROWTH|ENTERPRISE|入門|專業|企業|✨\s*POPULAR)$/i.test(l);

function parseSlide(text, slideNum, totalSlides) {
  if (!text) return { type: 'empty', headline: '', subtext: '', cards: [], stats: [], checks: [], crosses: [], tableData: null, pricingPlans: [], steps: [], lines: [] };

  const lines = text.split('\n').map(l => l.trim()).filter(l => l && !isJunk(l));

  const isCover = slideNum === 1;
  const isClosing = slideNum === totalSlides;

  if (isCover || isClosing) return { type: isCover ? 'cover' : 'closing', lines, cards: [], stats: [], checks: [], crosses: [], tableData: null, pricingPlans: [], steps: [] };

  // Pre-process: merge split ✗/✓ lines (✗ on own line + next line = description)
  const merged = [];
  let i = 0;
  while (i < lines.length) {
    const l = lines[i];
    if ((l === '✗' || l === '✓') && i + 1 < lines.length) {
      merged.push(`${l}  ${lines[i + 1]}`);
      i += 2;
    } else if (isEmojiOnly(l) && i + 1 < lines.length) {
      const title = lines[i + 1];
      const desc = (i + 2 < lines.length && !isEmojiOnly(lines[i + 2]) && !isJunk(lines[i + 2]) && !isStat(lines[i + 2]) && !lines[i + 2].startsWith('✗') && !lines[i + 2].startsWith('✓')) ? lines[i + 2] : '';
      merged.push(`${l} ${title}${desc ? '\n' + desc : ''}`);
      i += desc ? 3 : 2;
    } else if (isStepNum(l) && i + 1 < lines.length) {
      const title = lines[i + 1];
      const descParts = [];
      let j = i + 2;
      while (j < lines.length && !isStepNum(lines[j]) && !isEmojiOnly(lines[j]) && !isStat(lines[j]) && !isPlanName(lines[j]) && !lines[j].startsWith('✗') && !lines[j].startsWith('✓')) {
        descParts.push(lines[j]);
        j++;
      }
      merged.push(`STEP:${l}|${title}|${descParts.join(' ')}`);
      i = j;
    } else if (isPlanName(l)) {
      let planLabel = l;
      let j = i + 1;
      if (/✨/.test(l) && j < lines.length && isPlanName(lines[j])) {
        planLabel = `✨ ${lines[j]}`;
        j++;
      }
      const planLines = [planLabel];
      while (j < lines.length && !isPlanName(lines[j])) {
        planLines.push(lines[j]);
        j++;
      }
      merged.push(`PLAN:${planLines.join('|')}`);
      i = j;
    } else {
      merged.push(l);
      i++;
    }
  }

  // Now parse merged lines
  const cards = [], stats = [], checks = [], crosses = [], steps = [], pricingPlans = [];
  const textLines = [];
  let headline = '', subtext = '';
  const tableRows = [];

  // Detect comparison table: look for 3-column pattern (label, val1, val2)
  const hasComparisonMarkers = merged.some(l => /^(項目|Feature|คุณสมบัติ)$/.test(l));

  if (hasComparisonMarkers) {
    let startIdx = merged.findIndex(l => /^(項目|Feature|คุณสมบัติ)$/.test(l));
    if (startIdx >= 0) {
      // Lines before table are headline
      for (let k = 0; k < startIdx; k++) textLines.push(merged[k]);
      // Build 3-col table
      const tLines = merged.slice(startIdx);
      for (let k = 0; k < tLines.length; k += 3) {
        if (k + 2 < tLines.length) {
          tableRows.push([tLines[k], tLines[k + 1], tLines[k + 2]]);
        }
      }
    }
  }

  if (tableRows.length === 0) {
    for (const line of merged) {
      if (line.startsWith('STEP:')) {
        const parts = line.slice(5).split('|');
        steps.push({ num: parts[0], title: parts[1] || '', desc: parts[2] || '' });
        continue;
      }
      if (line.startsWith('PLAN:')) {
        const parts = line.slice(5).split('|');
        const name = (parts[0] || '').replace('✨ POPULAR', 'GROWTH ✨');
        const isPopular = parts[0]?.includes('POPULAR') || parts[0]?.includes('✨');
        const price = parts[1] || '';
        const features = parts.slice(2);
        pricingPlans.push({ name: isPopular ? name : parts[0], price, features, isPopular });
        continue;
      }

      if (line.startsWith('✗  ') || line.startsWith('✗ ')) {
        crosses.push(line.replace(/^✗\s*/, ''));
        continue;
      }
      if (line.startsWith('✓  ') || line.startsWith('✓ ')) {
        checks.push(line.replace(/^✓\s*/, ''));
        continue;
      }

      const emojiM = startsWithEmoji(line);
      if (emojiM) {
        const descLines = emojiM.rest.split('\n');
        cards.push({ emoji: emojiM.emoji, title: descLines[0], desc: descLines.slice(1).join(' ') });
        continue;
      }

      if (isStat(line)) {
        const stat = { value: line, label: '' };
        stats.push(stat);
        continue;
      }

      // Assign label to previous stat if stat has no label
      if (stats.length > 0 && !stats[stats.length - 1].label && !isStat(line) && !line.startsWith('✗') && !line.startsWith('✓')) {
        stats[stats.length - 1].label = line;
        continue;
      }

      if (line.includes('：') && (line.endsWith('：') || line.endsWith(':'))) {
        textLines.push(line);
        continue;
      }

      textLines.push(line);
    }
  }

  // Extract headline from first short text line
  if (textLines.length > 0 && textLines[0].length < 50) {
    headline = textLines.shift();
  }
  if (textLines.length > 0 && textLines[0].length < 80) {
    subtext = textLines.shift();
  }

  return {
    type: pricingPlans.length > 0 ? 'pricing' : steps.length > 0 ? 'steps' : tableRows.length > 0 ? 'comparison' : 'feature',
    headline, subtext, cards, stats, checks, crosses,
    tableData: tableRows.length > 0 ? tableRows : null,
    pricingPlans, steps, lines: textLines,
  };
}

function SlideContent({ slide, brandColor, totalSlides }) {
  const num = slide.slide_number;
  const title = (slide.title || '').replace(/^A$/, '');
  const content = (() => {
    if (!slide.content || !title) return slide.content;
    const firstLine = slide.content.split('\n')[0].trim();
    if (firstLine.toLowerCase() === title.trim().toLowerCase()) return slide.content.split('\n').slice(1).join('\n');
    return slide.content;
  })();
  const parsed = parseSlide(content, num, totalSlides);
  const accent = brandColor || '#00E5FF';
  const ar = (a) => hexToRgba(accent, a);

  const bgPalette = [
    'linear-gradient(135deg, #050714, #0a1628, #0d0d2b)',
    'linear-gradient(135deg, #070b1e, #0f1b3d)',
    'linear-gradient(160deg, #0a0e27, #141937)',
    'linear-gradient(135deg, #080c20, #0e1530)',
    'linear-gradient(145deg, #0b0f28, #131a38)',
  ];
  const isCover = parsed.type === 'cover';
  const isClosing = parsed.type === 'closing';
  const bg = isCover || isClosing
    ? 'linear-gradient(135deg, #050714 0%, #0a1628 40%, #0d0d2b 100%)'
    : bgPalette[(num - 1) % bgPalette.length];

  const titleParts = title.split('·').map(s => s.trim());

  return (
    <div style={{
      width: '100%', height: '100%', position: 'relative',
      background: bg, color: '#fff', overflow: 'hidden',
      display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
      padding: isCover ? '48px 6% 48px' : '36px 6% 48px',
    }}>
      {/* Decorative */}
      <div style={{ position: 'absolute', top: -150, right: -150, width: 450, height: 450, borderRadius: '50%', background: `radial-gradient(circle, ${ar(0.07)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: -120, left: -120, width: 380, height: 380, borderRadius: '50%', background: `radial-gradient(circle, ${ar(0.04)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

      {!isCover && !isClosing && (
        <div style={{ position: 'absolute', top: 16, right: '6%', fontSize: 11, color: 'rgba(255,255,255,0.15)', fontWeight: 600, letterSpacing: 2 }}>
          {String(num).padStart(2, '0')}
        </div>
      )}

      <div style={{ position: 'relative', zIndex: 1, maxWidth: 940, width: '100%', textAlign: isCover || isClosing ? 'center' : 'left' }}>

        {/* === COVER === */}
        {isCover && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ fontSize: 12, letterSpacing: 8, color: ar(0.6), marginBottom: 20, fontWeight: 600 }}>ALAYAESS</div>
            {parsed.lines.map((line, i) => {
              if (/升級|AI\s*員工|把整個|Upgrade|ยกระดับ/.test(line)) return <h1 key={i} style={{ fontSize: 'clamp(26px, 4.2vw, 48px)', fontWeight: 800, lineHeight: 1.15, margin: '6px 0', background: `linear-gradient(135deg, #fff 20%, ${accent})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{line}</h1>;
              if (/AI\s*WORKFORCE|AI\s*同事|AI\s*Workforce/.test(line)) return <p key={i} style={{ fontSize: 'clamp(13px, 1.6vw, 20px)', color: accent, fontWeight: 600, margin: '6px 0', letterSpacing: 4 }}>{line}</p>;
              if (/不請假|不抱怨|24\/7|ไม่ลา|doesn't/.test(line)) return <p key={i} style={{ fontSize: 'clamp(12px, 1.3vw, 16px)', color: 'rgba(255,255,255,0.4)', margin: '10px 0', letterSpacing: 2 }}>{line}</p>;
              if (/一套平台|三大難題|One platform|หนึ่งแพลตฟอร์ม/.test(line)) return <p key={i} style={{ fontSize: 'clamp(13px, 1.4vw, 17px)', color: 'rgba(255,255,255,0.5)', marginTop: 14, lineHeight: 1.6 }}>{line}</p>;
              if (/AlayaEss/i.test(line) && !/同事|Workforce/i.test(line)) return null;
              return <p key={i} style={{ fontSize: 'clamp(13px, 1.4vw, 17px)', color: 'rgba(255,255,255,0.45)', margin: '4px 0' }}>{line}</p>;
            })}
          </div>
        )}

        {/* === CLOSING === */}
        {isClosing && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ fontSize: 12, letterSpacing: 8, color: ar(0.5), marginBottom: 20, fontWeight: 600 }}>ALAYAESS</div>
            {parsed.lines.map((line, i) => {
              if (/競爭對手|已經在用|competitor|คู่แข่ง/.test(line)) return <h2 key={i} style={{ fontSize: 'clamp(22px, 3.2vw, 40px)', fontWeight: 800, lineHeight: 1.2, margin: '6px 0', background: `linear-gradient(135deg, #fff, ${accent})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{line}</h2>;
              if (/淘汰|不開始|eliminate|ถูกกำจัด/.test(line)) return <p key={i} style={{ fontSize: 'clamp(15px, 1.8vw, 22px)', color: 'rgba(255,255,255,0.55)', margin: '6px 0' }}>{line}</p>;
              if (/🚀|預約|demo|Demo|จอง/.test(line)) return <div key={i} style={{ display: 'inline-block', marginTop: 24, padding: '14px 40px', background: `linear-gradient(135deg, ${accent}, ${ar(0.7)})`, borderRadius: 8, fontSize: 'clamp(14px, 1.5vw, 18px)', fontWeight: 700, color: '#050714', boxShadow: `0 4px 24px ${ar(0.4)}` }}>{line.replace('🚀 ', '')}</div>;
              if (/@|📩/.test(line)) return <p key={i} style={{ fontSize: 'clamp(12px, 1.2vw, 15px)', color: 'rgba(255,255,255,0.35)', marginTop: 16 }}>{line}</p>;
              if (/免費|退費|負責|free|refund/.test(line)) return <p key={i} style={{ fontSize: 'clamp(11px, 1vw, 13px)', color: 'rgba(255,255,255,0.25)', marginTop: 12, letterSpacing: 1 }}>{line}</p>;
              if (/Made for|post-employee/.test(line)) return <p key={i} style={{ fontSize: 10, color: 'rgba(255,255,255,0.15)', marginTop: 24, letterSpacing: 4, textTransform: 'uppercase' }}>{line}</p>;
              return <p key={i} style={{ fontSize: 'clamp(14px, 1.5vw, 17px)', color: 'rgba(255,255,255,0.45)', margin: '4px 0' }}>{line}</p>;
            })}
          </div>
        )}

        {/* === REGULAR SLIDES === */}
        {!isCover && !isClosing && (
          <>
            {/* Title */}
            {title && (
              <h2 style={{ fontSize: 'clamp(20px, 2.8vw, 36px)', fontWeight: 700, lineHeight: 1.2, marginBottom: 16 }}>
                {titleParts.length > 1 ? (
                  <>
                    <span style={{ color: accent, fontWeight: 800, fontSize: '0.65em', letterSpacing: 4, textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>{titleParts[0]}</span>
                    <span>{titleParts.slice(1).join(' · ')}</span>
                  </>
                ) : title}
              </h2>
            )}

            {/* Headline + subtext */}
            {parsed.headline && (
              <p style={{ fontSize: 'clamp(16px, 2.2vw, 28px)', fontWeight: 700, lineHeight: 1.3, marginBottom: 8 }}>
                {parsed.headline}
              </p>
            )}
            {parsed.subtext && (
              <p style={{ fontSize: 'clamp(13px, 1.3vw, 16px)', color: 'rgba(255,255,255,0.55)', lineHeight: 1.6, marginBottom: 16 }}>
                {parsed.subtext}
              </p>
            )}

            {/* Text lines */}
            {parsed.lines.length > 0 && parsed.type === 'feature' && (
              <div style={{ marginBottom: 16 }}>
                {parsed.lines.map((l, i) => (
                  <p key={i} style={{ fontSize: l.length < 40 ? 'clamp(14px, 1.5vw, 18px)' : 'clamp(12px, 1.2vw, 15px)', color: l.length < 40 ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.6)', lineHeight: 1.7, margin: '4px 0', fontWeight: l.length < 30 ? 600 : 300 }}>{l}</p>
                ))}
              </div>
            )}

            {/* Stats */}
            {parsed.stats.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(parsed.stats.length, 4)}, 1fr)`, gap: 12, marginBottom: 20, width: '100%' }}>
                {parsed.stats.map((s, i) => (
                  <div key={i} style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${ar(0.12)}`, borderRadius: 12, padding: '18px 14px', textAlign: 'center' }}>
                    <div style={{ fontSize: 'clamp(20px, 2.8vw, 34px)', fontWeight: 800, color: accent, lineHeight: 1.1, marginBottom: 4 }}>{s.value}</div>
                    {s.label && <div style={{ fontSize: 'clamp(10px, 1vw, 13px)', color: 'rgba(255,255,255,0.45)', lineHeight: 1.4 }}>{s.label}</div>}
                  </div>
                ))}
              </div>
            )}

            {/* Cards */}
            {parsed.cards.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${parsed.cards.length > 4 ? 180 : 220}px, 1fr))`, gap: 12, marginBottom: 16, width: '100%' }}>
                {parsed.cards.map((c, i) => (
                  <div key={i} style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid rgba(255,255,255,0.07)`, borderRadius: 12, padding: '16px 14px', borderLeft: `3px solid ${ar(0.35)}` }}>
                    <div style={{ fontSize: 22, marginBottom: 6 }}>{c.emoji}</div>
                    <div style={{ fontSize: 'clamp(12px, 1.15vw, 15px)', fontWeight: 700, color: accent, marginBottom: 4, lineHeight: 1.3 }}>{c.title}</div>
                    {c.desc && <div style={{ fontSize: 'clamp(10px, 0.95vw, 12px)', color: 'rgba(255,255,255,0.5)', lineHeight: 1.5 }}>{c.desc}</div>}
                  </div>
                ))}
              </div>
            )}

            {/* Checks / Crosses */}
            {(parsed.checks.length > 0 || parsed.crosses.length > 0) && (
              <div style={{ display: 'grid', gridTemplateColumns: parsed.checks.length > 0 && parsed.crosses.length > 0 ? '1fr 1fr' : '1fr', gap: 16, marginBottom: 16, width: '100%' }}>
                {parsed.crosses.length > 0 && (
                  <div style={{ background: 'rgba(255,50,50,0.05)', border: '1px solid rgba(255,50,50,0.12)', borderRadius: 12, padding: '16px 18px' }}>
                    {parsed.checks.length > 0 && <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,80,80,0.7)', marginBottom: 8, letterSpacing: 2 }}>BEFORE</div>}
                    {parsed.crosses.map((item, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, fontSize: 'clamp(11px, 1.05vw, 13px)', color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>
                        <span style={{ color: '#ff4d6a', flexShrink: 0, fontWeight: 700 }}>✗</span><span>{item}</span>
                      </div>
                    ))}
                  </div>
                )}
                {parsed.checks.length > 0 && (
                  <div style={{ background: ar(0.04), border: `1px solid ${ar(0.12)}`, borderRadius: 12, padding: '16px 18px' }}>
                    {parsed.crosses.length > 0 && <div style={{ fontSize: 11, fontWeight: 700, color: accent, marginBottom: 8, letterSpacing: 2 }}>AFTER</div>}
                    {parsed.checks.map((item, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, fontSize: 'clamp(11px, 1.05vw, 13px)', color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>
                        <span style={{ color: accent, flexShrink: 0, fontWeight: 700 }}>✓</span><span>{item}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Comparison table */}
            {parsed.tableData && (
              <div style={{ width: '100%', overflowX: 'auto', marginBottom: 16 }}>
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, borderRadius: 12, overflow: 'hidden' }}>
                  <thead>
                    <tr>
                      {parsed.tableData[0].map((cell, ci) => (
                        <th key={ci} style={{ background: ar(0.18), color: '#fff', padding: '10px 14px', fontWeight: 700, fontSize: 'clamp(11px, 1.05vw, 13px)', textAlign: ci === 0 ? 'left' : 'center', borderBottom: `2px solid ${ar(0.25)}` }}>{cell}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.tableData.slice(1).map((row, ri) => (
                      <tr key={ri}>
                        {row.map((cell, ci) => (
                          <td key={ci} style={{ padding: '8px 14px', background: ri % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent', borderBottom: '1px solid rgba(255,255,255,0.05)', fontSize: 'clamp(11px, 1.05vw, 13px)', textAlign: ci === 0 ? 'left' : 'center', fontWeight: ci === 0 ? 600 : 400, color: ci === 2 ? accent : 'rgba(255,255,255,0.7)' }}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Steps / Timeline */}
            {parsed.steps.length > 0 && (
              <div style={{ display: 'flex', gap: 0, width: '100%', marginBottom: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
                {parsed.steps.map((step, i) => (
                  <div key={i} style={{ flex: `1 1 ${parsed.steps.length > 4 ? '140px' : '180px'}`, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', position: 'relative', padding: '0 8px', minWidth: 0 }}>
                    <div style={{ width: 44, height: 44, borderRadius: '50%', background: `linear-gradient(135deg, ${accent}, ${ar(0.6)})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800, color: '#050714', marginBottom: 10, flexShrink: 0, boxShadow: `0 3px 16px ${ar(0.35)}` }}>
                      {step.num.replace(/^0/, '').replace(/^Day\s*/i, 'D')}
                    </div>
                    {i < parsed.steps.length - 1 && (
                      <div style={{ position: 'absolute', top: 22, left: 'calc(50% + 26px)', width: 'calc(100% - 52px)', height: 2, background: `linear-gradient(90deg, ${ar(0.4)}, rgba(255,255,255,0.08))` }} />
                    )}
                    <div style={{ fontSize: 'clamp(11px, 1.1vw, 14px)', fontWeight: 700, color: accent, marginBottom: 4, lineHeight: 1.2 }}>{step.title}</div>
                    {step.desc && <div style={{ fontSize: 'clamp(9px, 0.9vw, 11px)', color: 'rgba(255,255,255,0.45)', lineHeight: 1.4 }}>{step.desc}</div>}
                  </div>
                ))}
              </div>
            )}

            {/* Pricing */}
            {parsed.pricingPlans.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(parsed.pricingPlans.length, 3)}, 1fr)`, gap: 14, width: '100%', marginBottom: 16 }}>
                {parsed.pricingPlans.map((plan, i) => (
                  <div key={i} style={{
                    background: plan.isPopular ? ar(0.08) : 'rgba(255,255,255,0.03)',
                    border: plan.isPopular ? `2px solid ${ar(0.4)}` : '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 14, padding: '22px 18px', textAlign: 'center',
                    position: 'relative', overflow: 'hidden',
                    transform: plan.isPopular ? 'scale(1.03)' : 'none',
                    boxShadow: plan.isPopular ? `0 4px 24px ${ar(0.2)}` : 'none',
                  }}>
                    {plan.isPopular && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${accent}, ${ar(0.5)})` }} />}
                    <div style={{ fontSize: 'clamp(12px, 1.1vw, 14px)', fontWeight: 700, color: plan.isPopular ? accent : 'rgba(255,255,255,0.6)', letterSpacing: 2, marginBottom: 8 }}>
                      {plan.name.replace('✨', '').trim()}
                    </div>
                    <div style={{ fontSize: 'clamp(20px, 2.5vw, 30px)', fontWeight: 800, color: '#fff', marginBottom: 14 }}>{plan.price}</div>
                    {plan.features.map((f, fi) => (
                      <div key={fi} style={{ fontSize: 'clamp(10px, 1vw, 12px)', color: 'rgba(255,255,255,0.55)', padding: '4px 0', borderBottom: fi < plan.features.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>{f}</div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function SectionPresentation({ slug, section, siteInfo }) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const containerRef = useRef(null);
  const timerRef = useRef(null);

  const data = section.content_data || {};
  const slides = data.slides || [];
  const brandColor = siteInfo?.brand_color || '#00E5FF';
  const total = slides.length;

  const goTo = useCallback((idx) => { if (idx >= 0 && idx < total) setCurrent(idx); }, [total]);
  const next = useCallback(() => setCurrent(c => c < total - 1 ? c + 1 : 0), [total]);
  const prev = useCallback(() => setCurrent(c => c > 0 ? c - 1 : total - 1), [total]);

  useEffect(() => {
    if (isPlaying) { timerRef.current = setInterval(next, 7000); return () => clearInterval(timerRef.current); }
    clearInterval(timerRef.current);
  }, [isPlaying, next]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); next(); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    if (isFullscreen) { window.addEventListener('keydown', handler); return () => window.removeEventListener('keydown', handler); }
  }, [isFullscreen, next, prev]);

  const toggleFullscreen = () => {
    if (!isFullscreen) { containerRef.current?.requestFullscreen?.(); setIsFullscreen(true); }
    else { document.exitFullscreen?.(); setIsFullscreen(false); }
  };

  useEffect(() => {
    const fn = () => { if (!document.fullscreenElement) setIsFullscreen(false); };
    document.addEventListener('fullscreenchange', fn);
    return () => document.removeEventListener('fullscreenchange', fn);
  }, []);

  if (!slides.length) return null;

  const accent = brandColor;
  const ar = (a) => hexToRgba(accent, a);
  const progressPct = ((current + 1) / total) * 100;

  return (
    <div style={{ maxWidth: isFullscreen ? '100%' : 1200, margin: '0 auto', padding: isFullscreen ? 0 : '48px 24px' }}>
      {!isFullscreen && (
        <>
          <Title level={2} style={{ textAlign: 'center', marginBottom: 8, fontWeight: 300, letterSpacing: 1 }}>
            {section.title || t('sections.presentation.title')}
          </Title>
          {section.subtitle && <Paragraph style={{ textAlign: 'center', color: '#666', marginBottom: 24 }}>{section.subtitle}</Paragraph>}
        </>
      )}

      <div ref={containerRef} style={{
        position: isFullscreen ? 'fixed' : 'relative',
        inset: isFullscreen ? 0 : undefined,
        zIndex: isFullscreen ? 9999 : undefined,
        borderRadius: isFullscreen ? 0 : 16,
        overflow: 'hidden',
        boxShadow: isFullscreen ? 'none' : `0 8px 48px rgba(0,0,0,0.3), 0 2px 8px ${ar(0.15)}`,
        background: '#050714',
        aspectRatio: isFullscreen ? undefined : '16 / 9',
        minHeight: isFullscreen ? '100vh' : undefined,
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Slides container */}
        <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          {slides.map((slide, i) => (
            <div key={i} style={{
              position: 'absolute', inset: 0,
              transition: 'opacity 0.5s ease, transform 0.5s ease',
              opacity: i === current ? 1 : 0,
              transform: i === current ? 'scale(1)' : (i < current ? 'scale(1.02)' : 'scale(0.98)'),
              pointerEvents: i === current ? 'auto' : 'none',
              overflowY: 'auto',
            }}>
              <SlideContent slide={slide} brandColor={brandColor} totalSlides={total} />
            </div>
          ))}

          {/* Click zones */}
          <div onClick={prev} style={{ position: 'absolute', top: 0, left: 0, bottom: 40, width: '12%', cursor: 'w-resize', zIndex: 10 }} />
          <div onClick={next} style={{ position: 'absolute', top: 0, right: 0, bottom: 40, width: '12%', cursor: 'e-resize', zIndex: 10 }} />
        </div>

        {/* Navigation bar */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 16px', background: 'rgba(5,7,20,0.85)',
          backdropFilter: 'blur(12px)', position: 'relative', zIndex: 20,
          borderTop: `1px solid rgba(255,255,255,0.04)`,
        }}>
          {/* Progress bar */}
          <div style={{ position: 'absolute', top: -1, left: 0, right: 0, height: 2, background: 'rgba(255,255,255,0.04)' }}>
            <div style={{ height: '100%', width: `${progressPct}%`, background: `linear-gradient(90deg, ${accent}, ${ar(0.4)})`, transition: 'width 0.4s ease', borderRadius: 1 }} />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <NavBtn onClick={prev}><LeftOutlined /></NavBtn>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', fontWeight: 500, fontVariantNumeric: 'tabular-nums', minWidth: 44, textAlign: 'center' }}>
              {current + 1}<span style={{ margin: '0 3px', opacity: 0.5 }}>/</span>{total}
            </span>
            <NavBtn onClick={next}><RightOutlined /></NavBtn>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 3, flex: 1, justifyContent: 'center', padding: '0 12px' }}>
            {slides.map((_, i) => (
              <span key={i} onClick={() => goTo(i)} style={{
                width: i === current ? 18 : 5, height: 5, borderRadius: 3, cursor: 'pointer',
                background: i === current ? accent : 'rgba(255,255,255,0.12)',
                transition: 'all 0.3s ease',
                opacity: i === current ? 1 : 0.6,
              }} />
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <NavBtn onClick={() => setIsPlaying(p => !p)}>{isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}</NavBtn>
            <NavBtn onClick={toggleFullscreen}>{isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}</NavBtn>
          </div>
        </div>
      </div>
    </div>
  );
}

function NavBtn({ onClick, children }) {
  return (
    <button onClick={onClick} style={{
      background: 'none', border: 'none', color: 'rgba(255,255,255,0.45)',
      cursor: 'pointer', fontSize: 14, padding: '4px 6px', display: 'flex',
      alignItems: 'center', transition: 'color 0.2s',
    }}
    onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.8)'}
    onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}
    >
      {children}
    </button>
  );
}
